import React from 'react';
import CosmeticRepairRequestPage from './CosmeticRepairRequestPage';
import {
    CosmeticPageErrors,
    CosmeticProcessStages,
    CosmeticRepairRequestType,
    Panels
} from './CosmeticRepairRequestTypes';
import CosmeticRepairAddressPage from './CosmeticRepairAddressPage';
import CosmeticRepairRequestSummary from './CosmeticRepairRequestSummary';
import { AddressType, VehicleType } from '../../common/CommonTypes';
import LoadingContainer from '../../common/loadingContainer/LoadingContainer';
import { useTranslation } from 'react-i18next';

export type CosmeticRepairRequestProps = {
    cosmeticRequests: CosmeticRepairRequestType[];
    cosmeticProcessStage: CosmeticProcessStages;
    cosmeticFormError?: CosmeticPageErrors;
    currentCosmeticRepairRequest: CosmeticRepairRequestType;
    repairAddressSelectedId: string | null;
    loadingOnSubmitRequests: boolean;
    permissionGranted: boolean;
    vehicle: VehicleType;
    repairAddresses: Array<AddressType>;
    scrollToAddress?: boolean;
    showRepairRequestForm: boolean;
    showCancelAlert: boolean;
    showDamageDepthQuestion: () => boolean;
    onSaveCosmetic: () => boolean;
    onChange: (key: string, value: string) => void;
    resetGeneralErrors: () => void;
    onChangeMileage: (event: React.FormEvent<HTMLInputElement>) => void;
    onChangeDamageType: (selectionName: string) => void;
    onChangeDamageDepth: (selectedValue: string) => void;
    onAddCosmeticRequest: () => void;
    onUploadImageChange: (
        imageType: string,
        uploadedPath: string,
        imageId: string,
        image: string,
        filename: string
    ) => void;
    onContinueToRepairAddress: () => void;
    onRepairAddressSelectionChanged: (selectedId: string | null) => void;
    onRepairAddressAdded: (address: AddressType) => void;
    onRepairAddressUpdated: (id: string, address: AddressType) => void;
    onSubmitCosmeticRepairRequests: () => void;
    onUpdateCosmeticRequest: (requestId: number) => void;
    onSelectedPanelChanged: (selected: boolean, panel: Panels) => void;
    onContinueToCosmeticSummary: () => void;
    onUpdateAddressClickFromSummary: (scrollToAddress) => void;
    onChangeStage: (CosmeticProcessStages) => void;
    onUpdateCosmeticClickFromSummary: (requestId: number) => void;
    onShowRepairRequestForm: (show: boolean) => void;
    onTogglePermission: (isChecked: boolean) => void;
    onCancelClick: () => void;
    onCancelRepairRequest: () => void;
    onCancelUndo: () => void;
    onDeleteCosmeticRequest: (requestId: number) => void;
    onDefaultDealerAddress: (defaultDealerAddress: AddressType) => void;
};

const CosmeticRepairRequest = ({
    cosmeticRequests,
    scrollToAddress = false,
    showRepairRequestForm,
    currentCosmeticRepairRequest,
    cosmeticProcessStage,
    cosmeticFormError,
    repairAddressSelectedId,
    repairAddresses,
    loadingOnSubmitRequests,
    permissionGranted,
    vehicle,
    showCancelAlert,
    onChange,
    onSaveCosmetic,
    resetGeneralErrors,
    onChangeMileage,
    onChangeDamageType,
    onChangeDamageDepth,
    showDamageDepthQuestion,
    onAddCosmeticRequest,
    onUploadImageChange,
    onContinueToRepairAddress,
    onRepairAddressSelectionChanged,
    onRepairAddressAdded,
    onRepairAddressUpdated,
    onSubmitCosmeticRepairRequests,
    onUpdateCosmeticRequest,
    onSelectedPanelChanged,
    onContinueToCosmeticSummary,
    onUpdateAddressClickFromSummary,
    onChangeStage,
    onUpdateCosmeticClickFromSummary,
    onShowRepairRequestForm,
    onTogglePermission,
    onCancelClick,
    onCancelRepairRequest,
    onCancelUndo,
    onDeleteCosmeticRequest,
    onDefaultDealerAddress
}: CosmeticRepairRequestProps): JSX.Element => {
    const { t } = useTranslation();
    const cancelConfirmText = t('cancel_cosmetic_confirm_text');
    const selectedRepairAddress = repairAddresses.find(
        (a) => a.id === repairAddressSelectedId
    );

    return (
        <>
            {cosmeticProcessStage === CosmeticProcessStages.General && (
                <>
                    <CosmeticRepairRequestPage
                        cosmeticRequests={cosmeticRequests}
                        showRepairRequestForm={showRepairRequestForm}
                        repairRequest={currentCosmeticRepairRequest}
                        onChange={onChange}
                        cosmeticFormError={cosmeticFormError}
                        onConfirm={onSaveCosmetic}
                        resetErrors={resetGeneralErrors}
                        onChangeMileage={onChangeMileage}
                        onChangeDamageType={onChangeDamageType}
                        onChangeDamageDepth={onChangeDamageDepth}
                        showDamageDepthQuestion={showDamageDepthQuestion}
                        onAddCosmeticRequest={onAddCosmeticRequest}
                        onUploadImageChange={onUploadImageChange}
                        onContinueToRepairAddress={onContinueToRepairAddress}
                        onUpdateCosmeticRequest={onUpdateCosmeticRequest}
                        onSelectedPanelChanged={onSelectedPanelChanged}
                        onShowRepairRequestForm={onShowRepairRequestForm}
                        onCancelClick={onCancelClick}
                        cancelConfirmText={cancelConfirmText}
                        showCancelAlert={showCancelAlert}
                        onCancelRepairRequest={onCancelRepairRequest}
                        onCancelUndo={onCancelUndo}
                        onDeleteCosmeticRequest={onDeleteCosmeticRequest}
                    />
                </>
            )}
            {cosmeticProcessStage === CosmeticProcessStages.RepairAddress && (
                <CosmeticRepairAddressPage
                    cosmeticRequests={cosmeticRequests}
                    repairAddressSelectedId={repairAddressSelectedId}
                    repairAddresses={repairAddresses}
                    onRepairAddressSelectionChanged={
                        onRepairAddressSelectionChanged
                    }
                    onRepairAddressAdded={onRepairAddressAdded}
                    onRepairAddressUpdated={onRepairAddressUpdated}
                    onContinueToCosmeticSummary={onContinueToCosmeticSummary}
                    onBack={onChangeStage}
                    errors={cosmeticFormError?.cosmeticAddress}
                    scrollToAddress={scrollToAddress}
                    onCancelClick={onCancelClick}
                    cancelConfirmText={cancelConfirmText}
                    showCancelAlert={showCancelAlert}
                    onCancelRepairRequest={onCancelRepairRequest}
                    onCancelUndo={onCancelUndo}
                    onDefaultDealerAddressUpdate={onDefaultDealerAddress}
                />
            )}
            {cosmeticProcessStage === CosmeticProcessStages.Summary && (
                <LoadingContainer
                    loading={loadingOnSubmitRequests}
                    message={t('registering_requests')}>
                    <CosmeticRepairRequestSummary
                        cosmeticRepairRequests={cosmeticRequests}
                        cosmeticRequestAmount={cosmeticRequests.length}
                        selectedRepairAddress={selectedRepairAddress}
                        onSubmitCosmeticRepairRequests={
                            onSubmitCosmeticRepairRequests
                        }
                        permissionGranted={permissionGranted}
                        errors={cosmeticFormError}
                        vehicle={vehicle}
                        onTogglePermission={onTogglePermission}
                        onBack={onChangeStage}
                        onUpdateAddressClick={onUpdateAddressClickFromSummary}
                        onUpdateCosmeticClick={onUpdateCosmeticClickFromSummary}
                        onCancelClick={onCancelClick}
                        cancelConfirmText={cancelConfirmText}
                        showCancelAlert={showCancelAlert}
                        onCancelRepairRequest={onCancelRepairRequest}
                        onCancelUndo={onCancelUndo}
                    />
                </LoadingContainer>
            )}
        </>
    );
};

export default CosmeticRepairRequest;
